import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { withRouter, Redirect } from 'react-router-dom';

import CheckoutOrderingSteps from '../components/content/checkout/CheckoutOrderingSteps';
import CheckoutCompletedView from '../components/content/checkout/CheckoutCompletedView';
import FeedbackDialog from '../components/content/dialogs/FeedbackDialog';
import WorldpayNotificationDialog from '../components/content/dialogs/WorldpayNotificationDialog';
import { NotificationDialogContext } from '../components/context/NotificationDialogContext';

import ClearCartMutation from '../mutations/ClearCartMutation';

import { pathHasLoginToken } from '../utils/common';
import { trackEvent } from '../utils/ga-tracking';
import { HelpdeskWidgetContext } from '../components/content/HelpdeskWidget'

const CheckoutPage = (props) => {

  const [email, setEmail] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [sgOrderId, setSgOrderId] = useState(null);
  const [worldpayCallbackPath, setWorldpayCallbackPath] = useState(null);
  const [showWorldpayNotificationDialog, setShowWorldpayNotificationDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [hlpDskState, hlpDskDispatch] = useContext(HelpdeskWidgetContext);

  const { onOpenNotificationDialog } = React.useContext(NotificationDialogContext);

  const handleCheckoutCompleted = (orderId, email) => {
    handleClearCart();
    setCompleted(true);
    setSgOrderId(orderId);
    setEmail(email);

    trackEvent('Checkout', 'Completed checkout', 'Checkout successfull', {
      nonInteraction: true,
    });
  };

  const handleOpenWorldpayNotificationDialog = callbackPath => {
    setShowWorldpayNotificationDialog(true);
    setWorldpayCallbackPath(callbackPath);
  };

  const handleCloseWorldpayNotificationDialog = () => {
    if (worldpayCallbackPath === 'Pending') {
      handleClearCart();
    } else {
      props.history.replace('/checkout');
    }
    setShowWorldpayNotificationDialog(false);
    setWorldpayCallbackPath(null);
  };

  const handleCloseFeedbackDialog = () => {
    setShowFeedbackDialog(false);
  }

  const handleClearCart = () => {
    const { viewer, relay } = props;
    ClearCartMutation.commit(relay.environment, viewer.cart.id);
  };

  const handleOpenFeedbackDialog = () => {
    setShowFeedbackDialog(true);
  }

  const handleShowCart = () => {
    props.history.push('/cart');
  }

  const { viewer, match, isLoggedIn } = props;

  useEffect(() => {
    sgOrderId && hlpDskDispatch({ type: 'addToHistory', payload: `Order ID: ${sgOrderId}` })
  }, [sgOrderId])

  React.useEffect(() => {
    const { match, history, location, viewer } = props;

    if (pathHasLoginToken(location.search)) {
      history.replace(location.pathname);
    }

    if (match.params.callbackUrl) {
      if (viewer.cart && viewer.cart.order && viewer.cart.order.orderid) {
        switch (match.params.callbackUrl) {
          case 'worldpay-pending':
            handleOpenWorldpayNotificationDialog('Pending');
            return;
          case 'worldpay-cancel':
            handleOpenWorldpayNotificationDialog('Cancel');
            trackEvent('Checkout', 'Canceled payment', 'Worldpay payment canceled');
            return;
          case 'worldpay-failure':
            handleOpenWorldpayNotificationDialog('Failure');
            trackEvent('Checkout', 'Failed payment', 'Worldpay failed', {
              nonInteraction: true,
            });
            return;
          case 'worldpay-error':
            handleOpenWorldpayNotificationDialog('Error');
            trackEvent('Checkout', 'Failed payment', 'Worldpay error', {
              nonInteraction: true,
            });
            return;
          default:
            handleCheckoutCompleted(
              viewer.cart.order.orderid,
              viewer.cart.customer.email
            );
            return;
        }
      }
    }
  });

  if (!completed) {
    if (!viewer.cart) {
      return <Redirect to="/" />;
    }
    if (match.params.callbackUrl) {
      if (!viewer.cart.order || (viewer.cart.order && !viewer.cart.order.orderid)) {
        return <Redirect to="/checkout" />;
      }
    }
  }

  return (
    <>
      {onOpenNotificationDialog && (
        <div>
          <div className="checkout-page-background" />
          <div className="container-960 container-page checkout-page">
            <div className="checkout-container">
              {!completed && (
                <CheckoutOrderingSteps
                  cart={viewer.cart}
                  viewer={viewer}
                  isLoggedIn={isLoggedIn}
                  onCheckoutCompleted={handleCheckoutCompleted}
                  onShowCart={handleShowCart}
                  onOpenNotificationDialog={onOpenNotificationDialog}
                />
              )}
              {completed && (
                <CheckoutCompletedView
                  email={email}
                  orderId={sgOrderId}
                  onOpenFeedbackClick={handleOpenFeedbackDialog}
                />
              )}
            </div>
          </div>
          <WorldpayNotificationDialog
            open={showWorldpayNotificationDialog}
            onRequestClose={handleCloseWorldpayNotificationDialog}
            path={worldpayCallbackPath}
          />
          <FeedbackDialog
            open={showFeedbackDialog}
            showRating={true}
            onRequestClose={handleCloseFeedbackDialog}
          />
        </div>
      )}
    </>
  );

}

CheckoutPage.propTypes = {
  isLoggedIn: PropTypes.bool,
  viewer: PropTypes.object,
  relay: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  location: PropTypes.object,
};

export { CheckoutPage };

export default withRouter(
  createFragmentContainer(CheckoutPage, {
    viewer: graphql`
      fragment CheckoutPage_viewer on Viewer {
        cart {
          customer {
            email
          }
          id
          order {
            id
            orderid
          }
          ...CheckoutOrderingSteps_cart
        }
        ...CheckoutOrderingSteps_viewer
      }
    `,
  })
);
